import {BehaviorSubject, Observable} from "rxjs";
import {FormGroup} from "@angular/forms";
import {Router} from "@angular/router";
import {MerchantApi} from "../../common/apis/merchant-api";
import {isFormSubmitted} from "../../common/utilities/FormsHelper";
import {Field} from "../../common/enums/Field";
import {ValidationErrorType} from "../../common/enums/ValidationErrorType";
import {Injectable} from "@angular/core";
import {CustomerApi} from "../../common/apis/customer-api";
import {RegisterCustomerRequest} from "../../common/interfaces/RegisterCustomerRequest";

@Injectable({
  providedIn: 'root'
})
export class CustomerSignupService {
  public currentStepBs: BehaviorSubject<Step> = new BehaviorSubject<Step>(Step.BASIC_INFO)
  public currentStep$: Observable<Step> = this.currentStepBs.asObservable()

  public disableBackButton = false
  public disableNextButton = false
  public loadingNextButton = false

  public signUpForm: FormGroup
  public nameErrorMessage: string | null = null
  public businessNameErrorMessage: string | null = null
  public emailErrorMessage: string | null = null
  public passwordErrorMessage: string | null = null
  public signUpFormErrorMessage: string | null = null
  public enableSignUpFormFields: boolean = true

  // Verify Email
  public emailVerificationErrorMessage: string | null = null
  public emailVerificationSuccessMessage: string | null = null

  // Login Form
  public loginForm: FormGroup
  public loginEmailErrorMessage: string | null = null
  public loginPasswordErrorMessage: string | null = null
  public loginFormErrorMessage: string | null = null
  constructor(
    private router: Router,
    private customerApi: CustomerApi
  ) {
  }

  nextButtonClicked() {
    switch (this.currentStepBs.getValue()) {
      case this.Step.BASIC_INFO:
        // this.signUpFormSubmitted();
        if (isFormSubmitted(this.signUpForm)) {
          this.loadingNextButton = true
          this.enableSignUpFormFields = false

          let name = this.signUpForm.get('name')?.value
          let email = this.signUpForm.get('email')?.value
          let password = this.signUpForm.get('password')?.value
          let phoneNumber = this.signUpForm.get('mobile')?.value
          let countryCode = "20"

          let registerCustomerRequest: RegisterCustomerRequest = {
            name: name,
            email: email,
            password: password,
            countryCode: countryCode,
            phoneNumber: phoneNumber
          }

          this.customerApi.customerRegister(registerCustomerRequest).subscribe({
            next: () => {
              this.loadingNextButton = false
              this.enableSignUpFormFields = true
              this.resetSignUpFormErrorMessages()

              //Store form information in local storage & read it from confirmation page when initialized with variable.
              localStorage.setItem("name", name)
              localStorage.setItem("email", email)
              localStorage.setItem("password", password)

              this.currentStepBs.next(this.Step.EMAIL_CONFIRMATION);
              this.router.navigate(["/customers/signup/email-verification"])
            },
            error: error => {
              this.loadingNextButton = false
              this.enableSignUpFormFields = true
              this.resetSignUpFormErrorMessages()

              if (Array.isArray(error)) {
                // Handle each error separately
                error.map(singleError => {
                  console.log(singleError.errorType)
                  console.log(singleError.field)
                  if (singleError.field == Field.NAME) {
                    if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.nameErrorMessage = $localize`Invalid name format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.nameErrorMessage = $localize`Name is required.`
                    }
                  }
                  if (singleError.field == Field.EMAIL) {
                    if (singleError.errorType == ValidationErrorType.DUPLICATE) {
                      this.emailErrorMessage = $localize`This email is already registered.`
                    } else if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.emailErrorMessage = $localize`Invalid email format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.emailErrorMessage = $localize`Email is required.`
                    }
                  }
                  if (singleError.field == Field.PASSWORD) {
                    if (singleError.errorType == ValidationErrorType.INVALID_FORMAT) {
                      this.passwordErrorMessage = $localize`Invalid password format.`
                    } else if (singleError.errorType == ValidationErrorType.REQUIRED) {
                      this.passwordErrorMessage = $localize`Password is required.`
                    }
                  }
                });
              } else {
                console.log("error is NOT an array")
                console.log(error)
                //TODO: Check if type of error is string before printing
                this.signUpFormErrorMessage = $localize`Something went wrong. Please try again later or contact support.`
              }
            },
            complete: () => {
              // TODO: Remove if not needed.
            }
          })
        }
        break;
      case this.Step.EMAIL_CONFIRMATION:
        // TODO: Confirm Email with server.
        // This step will never happen as the next button should be disabled and the website is waiting for the customer
        // to click on the link from the email.
        // this.currentStepBs.next(this.Step.MERCHANT_MORE_INFO);
        // this.router.navigate(["/merchants/signup/more-info"])
        break;
    }
  }

  backButtonClicked() {
    switch (this.currentStepBs.getValue()) {
      case this.Step.BASIC_INFO:
        // TODO: Nothing to do. Probably go back to the merchant home page?
        break;
      case this.Step.EMAIL_CONFIRMATION:
        this.currentStepBs.next(this.Step.BASIC_INFO);
        this.router.navigate(["/customers/signup"]).then()
        break;
    }
  }

  public resendEmailButtonClicked() {
    console.log("resendEmailButtonClicked");
    let email = localStorage.getItem("email")
    if (email) {
      this.customerApi.resendEmailVerification(email).subscribe({
        next: () => {
          // Email resent successfully
          this.emailVerificationSuccessMessage = $localize`Email resent successfully.`
          this.emailVerificationErrorMessage = null
        },
        error: error => {
          console.log("Error")
          console.log(error)
          if (Array.isArray(error)) {
            // Handle each error separately
            console.log("error is an array")
            error.map(singleError => {
              if (singleError.rawMessage === "/Customer/Email/Try-Later") {
                this.emailVerificationErrorMessage = $localize`A request is already being processed. Please check your email, including the junk folder or try again later 5 minutes later.`
              }
            })
          } else {
            if (error.rawMessage === "/Customer/Email/Try-Later")
              this.emailVerificationErrorMessage = $localize`A request is already being processed. Please check your email, including the junk folder or try again later 5 minutes later.`
            else
              this.emailVerificationErrorMessage = $localize`An unknown error has occurred. Please try again later or contact support.`
          }

        }
      })
    }
  }

  public verifyEmail(token: string) {
    this.customerApi.verifyEmail(token).subscribe({
      next: () => {
        // Proceed to next step

        let email = localStorage.getItem("email")
        let password = localStorage.getItem("password")
        if (email && password) {
          this.customerApi.customerLogin(email, password).subscribe({
            next: () => {
              console.log("Logged in successfully")
              this.emailVerificationSuccessMessage = null
              // this.currentStepBs.next(this.Step.MERCHANT_MORE_INFO);
              this.router.navigate(["/"]).then()
            },
            error: error => {
              console.log("Failed to login")
              console.log(error)
            }
          })
        } else {
          console.log("Credentials not found.. Go to login")
          this.router.navigate(["/login"], { state: { data: 'customer' } })
        }
      },
      error: error => {
        console.log("Error")
        console.log(error)
        // TODO Handle this error "/User/Email/Try-Later" here && "/Customer/Email/Try-Later" in the customer registration page.
        this.emailVerificationErrorMessage = $localize`An unknown error has occurred. Please try again later or contact support.`
      }
    })
  }

  public readonly Step = Step;

  private resetSignUpFormErrorMessages() {
    this.nameErrorMessage = null
    this.businessNameErrorMessage = null
    this.emailErrorMessage = null
    this.passwordErrorMessage = null
    this.signUpFormErrorMessage = null
  }

  private resetLoginFormErrorMessages() {
    this.loginEmailErrorMessage = null
    this.loginPasswordErrorMessage = null
    this.loginFormErrorMessage = null
  }
}

export enum Step {
  BASIC_INFO,
  EMAIL_CONFIRMATION,
}
